import React, { useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { isDownKey, isUpKey } from 'core/utils/keyDown';
import { red, orange, white } from 'core/styles';

// styles from https://css-tricks.com/styling-cross-browser-compatible-range-inputs-css/
const StyledRange = styled.input.attrs(() => ({
  type: 'range'
}))`
  -webkit-appearance: none;
  margin: 18px 0;
  width: 80%;
  &:focus {
    outline: none;
  }
  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 8.4px;
    cursor: pointer;
    animate: 0.2s;
    box-shadow: 1px 1px 1px #000000, 0px 0px 1px #0d0d0d;
    background: ${white};
    border-radius: 1.3px;
    border: 0.2px solid #010101;
  }
  &::-webkit-slider-thumb {
    box-shadow: 1px 1px 1px #000000, 0px 0px 1px #0d0d0d;
    border: 1px solid #000000;
    height: 36px;
    width: 16px;
    border-radius: 3px;
    background: ${({ selected }) => (selected ? red : orange)};
    cursor: pointer;
    -webkit-appearance: none;
    margin-top: -14px;
  }
  &:focus::-webkit-slider-runnable-track {
    background: grey;
  }
  &::-moz-range-track {
    width: 100%;
    height: 8.4px;
    cursor: pointer;
    animate: 0.2s;
    box-shadow: 1px 1px 1px #000000, 0px 0px 1px #0d0d0d;
    background: ${white};
    border-radius: 1.3px;
    border: 0.2px solid #010101;
  }
  &::-moz-range-thumb {
    box-shadow: 1px 1px 1px #000000, 0px 0px 1px #0d0d0d;
    border: 1px solid #000000;
    height: 36px;
    width: 16px;
    border-radius: 3px;
    background: ${({ selected }) => (selected ? red : orange)};
    cursor: pointer;
  }
  &::-ms-track {
    width: 100%;
    height: 8.4px;
    cursor: pointer;
    animate: 0.2s;
    background: transparent;
    border-color: transparent;
    border-width: 16px 0;
    color: transparent;
  }
  &::-ms-fill-lower {
    background: #2a6495;
    border: 0.2px solid #010101;
    border-radius: 2.6px;
    box-shadow: 1px 1px 1px #000000, 0px 0px 1px #0d0d0d;
  }
  &::-ms-fill-upper {
    background: ${white};
    border: 0.2px solid #010101;
    border-radius: 2.6px;
    box-shadow: 1px 1px 1px #000000, 0px 0px 1px #0d0d0d;
  }
  &::-ms-thumb {
    box-shadow: 1px 1px 1px #000000, 0px 0px 1px #0d0d0d;
    border: 1px solid #000000;
    height: 36px;
    width: 16px;
    border-radius: 3px;
    background: ${({ selected }) => (selected ? red : orange)};
    cursor: pointer;
  }
  &:focus::-ms-fill-lower {
    background: ${white};
  }
  &:focus::-ms-fill-upper {
    background: grey;
  }
`;

const Range = ({
  value,
  onChange,
  min = 0,
  max = 100,
  selected,
  handleKeyDown,
  ...rest
}) => {
  const _onChange = e => {
    onChange(e.target.value);
  };
  const linkRef = useRef(null);
  useEffect(() => {
    if (selected) {
      linkRef.current.focus();
    } else {
      linkRef.current.blur();
    }
  }, [selected, linkRef]);

  const _handleKeyDown = useCallback(
    e => {
      if (handleKeyDown) {
        const overrideDefaults = handleKeyDown(e);
        if (overrideDefaults) return;
      }
      if (isUpKey(e)) {
        e.preventDefault();
      } else if (isDownKey(e)) {
        e.preventDefault();
      }
    },
    [handleKeyDown]
  );
  return (
    <StyledRange
      onKeyDown={_handleKeyDown}
      selected={selected}
      ref={linkRef}
      value={value}
      onChange={_onChange}
      min={min}
      max={max}
      {...rest}
    />
  );
};

export default Range;
